import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import TableHeaderFilter from "../components/TableHeaderfilter";
import { Bars3Icon, PlusIcon } from "@heroicons/react/24/outline";
import axios from "axios";
import { SERVER_URL } from "../config";
import { SalarySlipModal, GenerateSlipModal } from "../components/SalarySlipModal";
import { FaEye } from "react-icons/fa";
import MonthYearPicker from "../components/MonthYearPicker";

const SalaryManagementPage = () => {

    const [salaryData, setSalaryData] = useState([]);
    const [selectedSlip, setSelectedSlip] = useState(null);
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [filterEmployeeName, setFilterEmployeeName] = useState([]);
    const [filterEmployeeId, setFilterEmployeeId] = useState([]);
    const [filterGeneratedDate, setFilterGeneratedDate] = useState([]);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedYear, setSelectedYear] = useState(null);

    const fetchSalarySlips = async () => {
        try {
            const res = await axios.get(`${SERVER_URL}/Rahul/fetchSalarySlip.php`);
            setSalaryData(res.data || []);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchSalarySlips();
    }, []);

    const employeeNameOptions = [...new Set(salaryData.map(e => e.employeeName))]
        .filter(Boolean)
        .map(v => ({ label: v, value: v }));

    const employeeIdOptions = [...new Set(salaryData.map(e => e.employeeId))]
        .filter(Boolean)
        .map(v => ({ label: v, value: v }));

    const generatedDateOptions = [...new Set(salaryData.map(e => e.generatedDate))]
        .filter(Boolean)
        .map(v => ({ label: v, value: v }));

    const filteredSalaryData = salaryData.filter((item) => {
        const q = search.toLowerCase();
        const nameMatch = filterEmployeeName.length === 0 || filterEmployeeName.includes(item.employeeName);
        const idMatch = filterEmployeeId.length === 0 || filterEmployeeId.includes(item.employeeId);
        const dateMatch = filterGeneratedDate.length === 0 || filterGeneratedDate.includes(item.generatedDate);
        const searchMatch =
            !q ||
            String(item.employeeId || "").toLowerCase().includes(q) ||
            String(item.employeeName || "").toLowerCase().includes(q);

        let monthMatch = true;
        if (selectedMonth && selectedYear) {
            // Supports "YYYY-MM-DD". If your API returns "DD/MM/YYYY" swap to manual parse below.
            const d = new Date(item.generatedDate);
            monthMatch = (d.getMonth() + 1) === selectedMonth && d.getFullYear() === selectedYear;
            // ── DD/MM/YYYY alternative ──────────────────────────────────────────
            // const [dd, mm, yyyy] = (item.generatedDate || "").split("/");
            // monthMatch = +mm === selectedMonth && +yyyy === selectedYear;
        }

        return nameMatch && idMatch && dateMatch && searchMatch && monthMatch;
    });

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
                * { box-sizing: border-box; }
                body { font-family: 'Inter', sans-serif; }
                .salary-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
                .salary-scrollbar::-webkit-scrollbar-track { background: rgba(241,245,249,1); border-radius: 10px; }
                .salary-scrollbar::-webkit-scrollbar-thumb { background: rgba(148,163,184,0.5); border-radius: 10px; }
                .salary-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(148,163,184,0.8); }
            `}</style>

            <div className="min-h-screen w-full font-[Inter]" style={{ background: "#f0f4f8" }}>

                <div
                    className="fixed top-0 left-0 right-0 h-0.5 z-50"
                    style={{ background: "linear-gradient(90deg, #1e40af 0%, #0369a1 50%, #0e7490 100%)" }}
                />

                <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} onLogout={() => { localStorage.clear(); }} />

                {/* ── STICKY HEADER ── */}
                <div
                    className="sticky top-0 z-20 border-b border-slate-200"
                    style={{ background: "#ffffff", boxShadow: "0 1px 4px rgba(15,23,42,0.08)" }}
                >
                    <div className="px-7 py-3.5 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors border-0"
                            >
                                <Bars3Icon className="h-5 w-5" />
                            </button>
                            <div className="flex items-center gap-2.5">
                                <div
                                    className="w-1 h-6 rounded-full"
                                    style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }}
                                />
                                <h1 className="text-lg font-bold m-0 tracking-tight text-slate-800">
                                    Salary Management
                                </h1>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowGenerateModal(true)}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white border-0 transition-all"
                            style={{ background: "linear-gradient(135deg, #1e40af, #0369a1)" }}
                        >
                            <PlusIcon className="h-4 w-4" />
                            Generate Salary Slip
                        </button>
                    </div>
                </div>

                {/* ── MAIN CONTENT ── */}
                <div className="relative z-10 p-5 flex flex-col gap-4">

                    {/* ── FILTER BAR ── */}
                    <div
                        className="rounded-xl px-5 py-4 bg-white border border-slate-200"
                        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}
                    >
                        <div className="flex items-center gap-3 flex-wrap">

                            {/* Month/Year Picker */}
                            <MonthYearPicker
                                selectedMonth={selectedMonth}
                                selectedYear={selectedYear}
                                label="Filter by Month"
                                onChange={({ month, year }) => {
                                    setSelectedMonth(month);
                                    setSelectedYear(year);
                                }}
                            />

                            {/* Search */}
                            <input
                                type="text"
                                placeholder="Search by Emp ID or Name"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="w-52 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 h-10 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors"
                            />
                        </div>
                    </div>

                    {/* ── TABLE ── */}
                    <div
                        className="rounded-xl overflow-hidden bg-white border border-slate-200"
                        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}
                    >
                        <div className="overflow-x-auto overflow-y-auto max-h-[65vh] salary-scrollbar">
                            <table className="min-w-full w-full text-sm">

                                <thead className="sticky top-0 z-10" style={{ background: "#f8fafc" }}>
                                    <tr>
                                        <th className="p-3 text-left w-[35%] border-b border-slate-200">
                                            <TableHeaderFilter
                                                label="Employee Name"
                                                options={employeeNameOptions}
                                                selectedValues={filterEmployeeName}
                                                onChange={setFilterEmployeeName}
                                                fullWidth
                                            />
                                        </th>
                                        <th className="p-3 text-left w-[25%] border-b border-slate-200">
                                            <TableHeaderFilter
                                                label="Employee ID"
                                                options={employeeIdOptions}
                                                selectedValues={filterEmployeeId}
                                                onChange={setFilterEmployeeId}
                                                fullWidth
                                            />
                                        </th>
                                        <th className="p-3 text-left w-[25%] border-b border-slate-200">
                                            <span className="text-[13px] font-bold text-slate-500 tracking-wider">Generate date</span>
                                        </th>
                                        <th className="p-3 text-center w-[15%] border-b border-slate-200">
                                            <span className="text-[13px] font-bold text-slate-500 tracking-wider">View</span>
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {filteredSalaryData.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="p-8 text-center text-slate-400 text-sm">
                                                No salary slips found.
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredSalaryData.map((item) => (
                                            <tr
                                                key={item.id}
                                                className="border-t border-slate-100 transition-colors hover:bg-blue-50/60"
                                            >
                                                <td className="p-3 text-slate-800 truncate text-xs font-medium">{item.employeeName}</td>
                                                <td className="p-3 text-slate-500 text-xs font-medium">{item.employeeId}</td>
                                                <td className="p-3 text-slate-500 text-xs">{item.generatedDate}</td>
                                                <td className="p-3 text-center">
                                                    <button
                                                        onClick={() => setSelectedSlip(item.id)}
                                                        className="p-2 rounded-lg text-blue-500 hover:text-blue-700 hover:bg-blue-50 text-base transition-all"
                                                        title="View Salary Slip"
                                                    >
                                                        <FaEye />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                </div>
            </div>

            {/* Modals */}
            {selectedSlip && (
                <SalarySlipModal
                    slipId={selectedSlip}
                    onClose={() => setSelectedSlip(null)}
                />
            )}

            {showGenerateModal && (
                <GenerateSlipModal
                    onClose={() => setShowGenerateModal(false)}
                    onSuccess={fetchSalarySlips}
                />
            )}
        </>
    );
};

export default SalaryManagementPage;